import React from "react";
import { Check, Star } from "lucide-react";

const tiers = [
  {
    name: "Starter",
    prefix: "from",
    price: "£10",
    cadence: "/ month",
    tagline: "For sole traders & side hustles.",
    popular: false,
    features: [
      "Self Assessment tax return",
      "Bookkeeping (up to 50 transactions/mo)",
      "Year-end summary",
      "HMRC correspondence handled",
      "Email support",
    ],
    cta: "Start with Starter",
  },
  {
    name: "Business",
    prefix: "from",
    price: "£25",
    cadence: "/ month",
    tagline: "For limited companies & growing teams.",
    popular: true,
    features: [
      "Statutory accounts & Corporation Tax",
      "VAT returns (MTD)",
      "Payroll for up to 3 employees",
      "Director's Self Assessment",
      "Quarterly tax planning call",
    ],
    cta: "Choose Business",
  },
  {
    name: "Premium",
    prefix: "from",
    price: "£50",
    cadence: "/ month",
    tagline: "For established businesses scaling fast.",
    popular: false,
    features: [
      "Everything in Business",
      "Monthly management accounts",
      "Unlimited payroll",
      "Dedicated account manager",
      "Priority same-day response",
    ],
    cta: "Go Premium",
  },
];

export const Pricing = () => (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="relative bg-[#EAF4DD] text-[#2A3430] overflow-hidden"
  >
    <div className="pointer-events-none absolute -top-32 -right-20 h-96 w-96 rounded-full bg-[#C57A5A]/8 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C57A5A] font-medium">
          Pricing
        </p>
        <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-5xl leading-tight text-[#2A3430]">
          Simple monthly plans. <span className="italic">No surprises.</span>
        </h2>
        <p className="mt-4 text-[#2A3430]/70">
          Fixed monthly fees, billed by direct debit. Cancel any time — though
          we’re fairly confident you won’t want to.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {tiers.map((t) => {
          const popular = t.popular;
          return (
            <div
              key={t.name}
              data-testid={`pricing-card-${t.name.toLowerCase()}`}
              className={[
                "relative rounded-3xl p-8 flex flex-col transition-all duration-300",
                popular
                  ? "bg-[#2A3430] text-white md:scale-[1.04] shadow-2xl shadow-[#2A3430]/25 ring-1 ring-[#C57A5A]/40"
                  : "bg-white ring-1 ring-[#2A3430]/10 hover:-translate-y-1 hover:shadow-xl hover:ring-[#C57A5A]/40",
              ].join(" ")}
            >
              {popular && (
                <span
                  data-testid="pricing-most-popular-badge"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#C57A5A] text-white text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full shadow-lg"
                >
                  <Star className="h-3 w-3 fill-white" /> Most Popular
                </span>
              )}

              <h3 className={`font-serif-display text-2xl ${popular ? "text-white" : "text-[#2A3430]"}`}>
                {t.name}
              </h3>
              <p className={`mt-1 text-sm ${popular ? "text-white/70" : "text-[#2A3430]/70"}`}>
                {t.tagline}
              </p>

              <div className="mt-6 flex items-baseline gap-1.5">
                <span className={`text-sm font-medium ${popular ? "text-white/70" : "text-[#2A3430]/60"}`}>
                  {t.prefix}
                </span>
                <span className={`font-serif-display text-5xl ${popular ? "text-white" : "text-[#2A3430]"}`}>
                  {t.price}
                </span>
                <span className={popular ? "text-white/60" : "text-[#2A3430]/60"}>
                  {t.cadence}
                </span>
              </div>

              <ul className="mt-7 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${popular ? "text-white/90" : "text-[#2A3430]"}`}>
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C57A5A]" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={[
                  "mt-8 inline-flex items-center justify-center font-medium px-6 py-3 rounded-full transition-all duration-300",
                  popular
                    ? "bg-[#C57A5A] hover:bg-[#A96444] text-white shadow-md hover:shadow-lg hover:shadow-[#C57A5A]/30"
                    : "bg-[#2A3430] text-white hover:bg-[#1F2A22]",
                ].join(" ")}
              >
                {t.cta}
              </a>
            </div>
          );
        })}
      </div>

      <p
        data-testid="pricing-disclaimer"
        className="mt-10 text-center text-xs text-[#2A3430]/55"
      >
        All prices + VAT. One-off services quoted separately.
      </p>
    </div>
  </section>
);

export default Pricing;
